import { ChevronRight, type LucideIcon } from 'lucide-react'
import { useId, useState } from 'react'
import './record-actions.css'

export type RecordAction = {
  /** What pressing it does, as a verb: Record a payment, Void charge. */
  label: string
  /** The glyph for what kind of act it is, read before the label. */
  icon: LucideIcon
  /** A figure the act concerns, set at the trailing edge: $180 owed. */
  figure?: string
  /** That something is behind it, as a mark rather than a number; a figure
   *  outranks it. */
  holding?: boolean
  /** Destruction: voiding, declining, deleting, closing for good. */
  danger?: boolean
  /** Why the act is not open yet. Present, pressing the row says this
   *  instead of acting. */
  unavailable?: string | null
  onSelect: () => void
}

/**
 * What can be done TO a record, as opposed to saving what was typed into it.
 *
 * A sheet's foot commits the form; these act on the record itself — pay toward
 * it, turn it into something else, void it. They are one titled list at the
 * end of the body, the same on every record, so an act is found in one place
 * rather than wherever the screen that grew it happened to put it.
 *
 * A row is a glyph and a verb, and a figure where the act concerns one. What
 * an act costs is said by its confirmation, where it is read at the moment it
 * matters, so a row does not say it twice.
 *
 * An act that is not open stays in the list, receded, and pressing it opens
 * the reason beneath it rather than acting. It stays a live control
 * (`aria-disabled`, not `disabled`) so the keyboard reaches it too: a button
 * that cannot take focus hides its reason from anybody not using a mouse.
 *
 * Destructive acts sort last whatever order they are passed in: the end of the
 * list is reached by reading past every ordinary one, which is the right amount
 * of deliberate for voiding a charge. Each still confirms at its caller.
 */
export function RecordActions({
  title = 'Actions',
  actions,
  busy = false,
}: {
  title?: string
  actions: readonly RecordAction[]
  /** An act is in flight; every row waits for it. */
  busy?: boolean
}) {
  const [explaining, setExplaining] = useState<string | null>(null)
  const reasonId = useId()
  if (actions.length === 0) return null
  const ordered = [
    ...actions.filter((a) => !a.danger),
    ...actions.filter((a) => a.danger),
  ]
  return (
    <section className="record-actions" aria-label={title}>
      <h3 className="record-actions__title">{title}</h3>
      <ul className="record-actions__rows">
        {ordered.map(
          ({
            label,
            icon: Icon,
            figure,
            holding,
            danger,
            unavailable,
            onSelect,
          }) => {
            const open = !!unavailable && explaining === label
            return (
              <li key={label}>
                <button
                  type="button"
                  className="record-actions__row"
                  data-danger={danger ? '' : undefined}
                  disabled={busy}
                  aria-disabled={unavailable ? true : undefined}
                  aria-expanded={unavailable ? open : undefined}
                  aria-describedby={open ? reasonId : undefined}
                  onClick={() =>
                    unavailable
                      ? setExplaining(open ? null : label)
                      : onSelect()
                  }
                >
                  <Icon
                    size={16}
                    aria-hidden
                    className="record-actions__icon"
                  />
                  <span className="record-actions__label">{label}</span>
                  {figure ? (
                    <span className="record-actions__figure">{figure}</span>
                  ) : holding ? (
                    <span className="record-actions__holding" aria-hidden />
                  ) : null}
                  <ChevronRight
                    size={16}
                    aria-hidden
                    className="record-actions__chevron"
                  />
                </button>
                {open ? (
                  <p id={reasonId} className="record-actions__reason">
                    {unavailable}
                  </p>
                ) : null}
              </li>
            )
          },
        )}
      </ul>
    </section>
  )
}
